import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { SorPicker, type PickedReceiver } from '@/components/patient/ReferralSorPicker';
import { HospitalReferralOutbox } from '@/components/patient/HospitalReferralOutbox';
import { ReferralFormChrome } from '@/components/patient/ReferralFormLayout';
import { ZipContentSelect } from '@/components/patient/ZipContentSelect';
import type { HospitalReferralPatient } from '@/components/patient/HospitalReferralForm';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { patientTabPath } from '@/lib/patientRecordPaths';
import { CodeChip } from '@/components/ui/Chip';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useClinic } from '@/hooks/useClinicSettings';
import { useClinicCommunicateConnection } from '@/hooks/useCareCommunications';
import { useDecryptCpr } from '@/hooks/usePatients';
import { ApiCallError, callFunction } from '@/lib/api';
import {
  XML_ATTACHMENT_ACCEPT,
  medbinObjectType,
  needsZipObjectType,
  type ZipObjectType,
} from '@/lib/referral/referralAttachments';
import { correspondenceIssues, CORRESPONDENCE_SUBJECT_MAX, CORRESPONDENCE_TEXT_MAX } from '@/lib/referral/correspondence';
import { correspondenceReceiverIdentity, cprDigits, readPostalAddress } from '@/lib/referral/xref01Validation';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

// C10 (BIN01-S 3.1): korrespondance XDIS91 (XD9134L) with the file as XBIN01
// (XB0131X, ns 2012/03/28). Communicate builds both; the XDIS91 lands in the
// Udbakke with the XBIN01 as its bilag ('Hent XML', 'Hent bilag', receipts).

type Check = { id: string; pass: boolean; detail: string };
type MedbinResult = {
  ok: boolean;
  checks: Check[];
  parentXml: string | null;
  parentExchangeId: string | null;
  exchangeId: string;
};

type Priority = 'rutine' | 'høj_prioritet';

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(binary);
}

export function CorrespondenceForm({
  patient,
  revealedCpr,
  onCprRevealed,
  canRevealCpr,
  onCancel,
  onCreated,
}: {
  patient: HospitalReferralPatient;
  revealedCpr: string | null;
  onCprRevealed: (cpr: string) => void;
  canRevealCpr: boolean;
  onCancel?: () => void;
  onCreated?: () => void;
}) {
  const { t } = useTranslation();
  const clinicQuery = useClinic(patient.clinic_id);
  const connectionQuery = useClinicCommunicateConnection(patient.clinic_id);
  const decryptPatient = useDecryptCpr();
  const [receiver, setReceiver] = useState<PickedReceiver | null>(null);
  const [priority, setPriority] = useState<Priority>('rutine');
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [zipType, setZipType] = useState<ZipObjectType | undefined>(undefined);
  const [attempted, setAttempted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checks, setChecks] = useState<Check[] | null>(null);
  const [revision, setRevision] = useState(0);
  const [cprFailed, setCprFailed] = useState(false);
  const onCprRevealedRef = useRef(onCprRevealed);
  onCprRevealedRef.current = onCprRevealed;

  useEffect(() => {
    if (!patient.has_cpr || revealedCpr || !canRevealCpr) return;
    let cancelled = false;
    void decryptPatient
      .mutateAsync({ patient_id: patient.id, source: 'correspondence', purpose: 'xdis91' })
      .then(
        (result) => {
          if (!cancelled) onCprRevealedRef.current(result.cpr);
        },
        () => {
          if (!cancelled) setCprFailed(true);
        },
      );
    return () => {
      cancelled = true;
    };
    // mutateAsync is stable; the mutation object is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRevealCpr, patient.has_cpr, patient.id, revealedCpr]);

  const cpr = revealedCpr ? cprDigits(revealedCpr) : null;
  const issues = correspondenceIssues({
    hasReceiver: Boolean(receiver?.organisation.ean),
    subject,
    text,
    hasFile: file !== null,
    zipTypeMissing: file !== null && needsZipObjectType(file.name) && !zipType,
    hasCpr: Boolean(cpr),
    hasSender: Boolean(connectionQuery.data?.ean && clinicQuery.data?.name),
  });

  async function onSubmit() {
    setAttempted(true);
    setError(null);
    if (issues.length > 0 || !file || !receiver || !cpr) return;
    const clinic = clinicQuery.data;
    const connection = connectionQuery.data;
    if (!clinic || !connection?.ean) return;
    setPending(true);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const address = readPostalAddress(patient.address);
      const objectType = medbinObjectType(file.name, zipType);
      const result = await callFunction<Record<string, unknown>, MedbinResult>('communicate-medbin', {
        clinicId: patient.clinic_id,
        patientId: patient.id,
        kind: 'XBIN01',
        sender: { ean: connection.ean, name: clinic.name },
        receiver: {
          ean: receiver.organisation.ean,
          name: receiver.organisation.name || receiver.organisation.ean,
          ...correspondenceReceiverIdentity({
            ean: receiver.organisation.ean,
            registerCode: receiver.organisation.identifierCode,
            chosenCode: receiver.chosenIdentifierCode,
            identifier: receiver.identifier,
          }),
        },
        patientCpr: cpr,
        patientGivenName: patient.first_name,
        patientSurname: patient.last_name,
        fileName: file.name,
        fileBase64: bytesToBase64(bytes),
        ...(objectType ? { objectType } : {}),
        correspondence: {
          priority,
          ...(subject.trim() ? { subject: subject.trim() } : {}),
          ...(text.trim() ? { text } : {}),
        },
        patientAddress: {
          ...(address.street ? { streetName: address.street.slice(0, 35) } : {}),
          ...(address.district ? { districtName: address.district.slice(0, 35) } : {}),
          ...(address.postCode ? { postCodeIdentifier: address.postCode.slice(0, 9) } : {}),
        },
      });
      setChecks(result.checks);
      const failed = result.checks.find((check) => !check.pass);
      if (!result.ok || failed || !result.parentExchangeId) {
        setError(failed?.detail || t('patients.correspondence.failed', 'Korrespondancen kunne ikke dannes.'));
        return;
      }
      setRevision((current) => current + 1);
      toast.success(t('patients.correspondence.built', 'Korrespondancen er dannet og ligger i udbakken.'));
      setSubject('');
      setText('');
      setFile(null);
      setZipType(undefined);
      setAttempted(false);
      onCreated?.();
    } catch (err) {
      setError(
        err instanceof ApiCallError
          ? err.message
          : t('patients.correspondence.failed', 'Korrespondancen kunne ikke dannes.'),
      );
    } finally {
      setPending(false);
    }
  }

  const show = (issue: string) => attempted && issues.includes(issue as (typeof issues)[number]);

  const patientName = `${patient.first_name} ${patient.last_name}`.trim();

  return (
    <div className="space-y-s5">
      <Card>
      <ReferralFormChrome
        backTo={patientTabPath(patient.id, 'medcom')}
        backLabel={t('patients.tabs.medcom')}
        title={t('patients.correspondence.title', 'Korrespondance med bilag (XDIS91 + XBIN01)')}
        subtitle={t('patients.correspondence.form_subtitle', 'Elektronisk korrespondance for {{name}}', {
          name: patientName,
        })}
        onCancel={onCancel}
        footer={
          <Button
            type="button"
            onClick={() => void onSubmit()}
            disabled={pending}
            loading={pending}
          >
            {t('patients.correspondence.submit', 'Dan korrespondance med bilag')}
          </Button>
        }
      >
        <div className="flex flex-wrap gap-s2">
          <CodeChip>XDIS91</CodeChip>
          <CodeChip>XBIN01</CodeChip>
        </div>
        <p className={FORM_HINT_TYPE}>
          {t(
            'patients.correspondence.intro',
            'Korrespondancen dannes som XDIS91, og filen sendes som bilag i en XBIN01, der kobles til korrespondancen med samme ObjectIdentifier.',
          )}
        </p>
        {cprFailed && (
          <p role="alert" className={FORM_ERROR_TYPE}>
            {t('patients.correspondence.cpr_failed', 'Patientens CPR kunne ikke hentes.')}
          </p>
        )}
        {show('cpr') && !cprFailed && (
          <p role="alert" className={FORM_ERROR_TYPE}>
            {t('patients.correspondence.cpr_missing', 'Patienten skal have et CPR-nummer.')}
          </p>
        )}
        {show('sender') && (
          <p role="alert" className={FORM_ERROR_TYPE}>
            {t('patients.correspondence.sender_missing', 'Klinikken har ingen MedCom-forbindelse med lokationsnummer.')}
          </p>
        )}

        <div>
          <SorPicker
            label={t('patients.correspondence.receiver', 'Modtager')}
            fieldId="correspondence-receiver"
            selected={receiver}
            partyLabel={t('patients.correspondence.receiver', 'Modtager')}
            receives={null}
            locationNumberFallback
            onSelect={(organisation) =>
              setReceiver(
                organisation.identifier && organisation.identifierCode
                  ? { organisation, identifier: organisation.identifier, chosenIdentifierCode: null }
                  : { organisation, identifier: organisation.ean, chosenIdentifierCode: 'lokationsnummer' },
              )
            }
            onIdentifier={(identifier) => setReceiver((current) => (current ? { ...current, identifier } : current))}
            onIdentifierCode={(chosenIdentifierCode) =>
              setReceiver((current) =>
                current
                  ? {
                      ...current,
                      chosenIdentifierCode,
                      identifier: chosenIdentifierCode === 'lokationsnummer' ? current.organisation.ean : current.identifier,
                    }
                  : current,
              )
            }
            onClear={() => setReceiver(null)}
          />
          {show('receiver') && (
            <p role="alert" className={cn('mt-2', FORM_ERROR_TYPE)}>
              {t('patients.correspondence.receiver_missing', 'Vælg en modtager med lokationsnummer.')}
            </p>
          )}
        </div>

        <div className="grid gap-s4 nav:grid-cols-3">
          <Select
            id="correspondence-priority"
            label={t('patients.correspondence.priority', 'Prioritet')}
            value={priority}
            onChange={(event) => setPriority(event.target.value === 'høj_prioritet' ? 'høj_prioritet' : 'rutine')}
          >
            <option value="rutine">{t('patients.correspondence.priority_rutine', 'Rutine')}</option>
            <option value="høj_prioritet">{t('patients.correspondence.priority_high', 'Høj prioritet')}</option>
          </Select>
          <Field
            className="nav:col-span-2"
            label={t('patients.correspondence.subject', 'Emne')}
            htmlFor="correspondence-subject"
            hint={`${subject.length} / ${CORRESPONDENCE_SUBJECT_MAX}`}
            hintId="correspondence-subject-hint"
          >
            <Input
              id="correspondence-subject"
              value={subject}
              maxLength={CORRESPONDENCE_SUBJECT_MAX}
              onChange={(event) => setSubject(event.target.value)}
            />
          </Field>
        </div>

        <Field
          label={t('patients.correspondence.text', 'Tekst')}
          htmlFor="correspondence-text"
          hint={`${text.length} / ${CORRESPONDENCE_TEXT_MAX}`}
          error={show('text') ? `${text.length} / ${CORRESPONDENCE_TEXT_MAX}` : undefined}
        >
          <Textarea
            id="correspondence-text"
            rows={8}
            value={text}
            onChange={(event) => setText(event.target.value)}
            className={show('text') ? 'border-[1.5px] border-ds-red' : undefined}
          />
        </Field>

        <Field label={t('patients.correspondence.file', 'Bilag (XBIN01)')} htmlFor="correspondence-file">
          <input
            id="correspondence-file"
            type="file"
            accept={XML_ATTACHMENT_ACCEPT}
            className={cn(TYPE.small, 'mt-s1 block text-ds-text')}
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setZipType(undefined);
            }}
          />
        </Field>
        {file && needsZipObjectType(file.name) && (
          <ZipContentSelect
            id="correspondence-zip-type"
            value={zipType}
            onChange={setZipType}
          />
        )}
        {show('file') && (
          <p role="alert" className={cn('mt-s2', FORM_ERROR_TYPE)}>
            {t('patients.correspondence.file_missing', 'Vælg en fil som bilag.')}
          </p>
        )}

        {error && (
          <p role="alert" className={FORM_ERROR_TYPE}>
            {error}
          </p>
        )}
      </ReferralFormChrome>
      {checks && (
        <ul className="space-y-s1">
          {checks.map((check) => (
            <li key={check.id} className={cn(TYPE.body, check.pass ? 'text-ds-green-ink' : 'text-ds-red-ink')}>
              {check.pass ? '✓' : '✗'} {check.detail}
            </li>
          ))}
        </ul>
      )}
      </Card>
      <HospitalReferralOutbox clinicId={patient.clinic_id} patientId={patient.id} revision={revision} />
    </div>
  );
}
